<template>
  <DefaultModalsContainer v-if="isOpen">
    <template v-slot:heading>
      <div class="modal__head">
        <h2 class="text-lg md:text-xl font-semibold text-gray-900">
          {{ $t('modal.rulesConsent.heading') }}
        </h2>
        <button
          @click="closeModal"
          class="p-1 hover:bg-gray-100 rounded-full transition-colors"
          type="button"
        >
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </template>
    <template v-slot:content>
      <div class="modal__body scrollbar-hide h-full">
        <div class="modal__copy">
          <p class="text-sm">{{ $t('modal.rulesConsent.copy') }}</p>
        </div>

        <UForm :validate="validate" :validate-on="['submit', 'change']" :state="state" class="modal__form" @submit="onSubmit" ref="form">
          <div class="mb-6">
            <div class="field field--checkbox">
              <label for="rulesConsent">
                <input
                  id="rulesConsent"
                  v-model="state.consent"
                  type="checkbox"
                  @change="updateValidation"
                />
                <div>
                  <p class="font-semibold mb-2">{{ $t('modal.rulesConsent.checkbox-prefix') }}</p>
                  <ul class="list-disc pl-4 space-y-1">
                    <li>{{ $t('modal.rulesConsent.checkbox-item-age') }}</li>
                    <li>
                      {{ $t('modal.rulesConsent.checkbox-item-rules-prefix') }}<a
                        href="https://www.cncenter.cz/pravidla-hry-bleskove-hry"
                        target="_blank"
                        rel="noopener"
                        @click.stop
                      >{{ $t('modal.rulesConsent.checkbox-item-rules-link') }}</a>{{ $t('modal.rulesConsent.checkbox-item-rules-suffix') }}
                    </li>
                    <li>
                      {{ $t('modal.rulesConsent.checkbox-item-privacy-prefix') }}<a
                        href="https://www.cncenter.cz/informace-o-zpracovani-akce"
                        target="_blank"
                        rel="noopener"
                        @click.stop
                      >{{ $t('modal.rulesConsent.checkbox-item-privacy-link') }}</a>{{ $t('modal.rulesConsent.checkbox-item-privacy-suffix') }}
                    </li>
                  </ul>
                </div>
              </label>

              <p v-for="error in getErrors('consent')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message }}
              </p>
            </div>
          </div>

          <div v-if="errorMessage" class="text-primary-500 text-sm mb-4">
            {{ errorMessage }}
          </div>

          <div class="modal__action">
            <button
              @click="closeModal"
              type="button"
              class="btn btn--border"
              :disabled="isLoading"
            >
              {{ $t('modal.rulesConsent.btn-cancel') }}
            </button>

            <button
              type="submit"
              class="btn btn--primary"
              :disabled="isLoading || !state.consent"
              :loading="isLoading"
            >
              {{ $t('modal.rulesConsent.btn-submit') }}
            </button>
          </div>
        </UForm>
      </div>
    </template>
  </DefaultModalsContainer>
</template>

<script setup lang="ts">
import type { FormError } from '#ui/types'
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  isOpen: { type: Boolean, required: false, default: false },
  isLoading: { type: Boolean, required: false, default: false },
  errorMessage: { type: String, required: false, default: undefined },
})

const emits = defineEmits(['close', 'confirm'])

const state = reactive({
  consent: false,
})

const hasChanged = ref(false)

const form = ref()

const getErrors = (path?: string) => {
  if (!path) {
    return form.value?.getErrors()
  }
  return form.value?.getErrors(path) || []
}

const updateValidation = () => {
  hasChanged.value = true
  form.value?.validate({ silent: true })
}

const validate = (formState: typeof state): FormError[] => {
  const errors: FormError[] = []
  if (hasChanged.value && !formState.consent) {
    errors.push({ path: 'consent', message: t('modal.rulesConsent.error-consent-required') })
  }
  return errors
}

const onSubmit = () => {
  if (!state.consent) return
  emits('confirm')
}

const closeModal = () => {
  emits('close')
}
</script>
